import { useState, useEffect, useRef } from "react";
import { Crown, Menu, X, Users, Home, Settings, Receipt, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { motion, AnimatePresence } from "motion/react";
import { LoginSignup } from "./LoginSignup";
import { BookingPage } from "./BookingPage";
import { AnnouncementsPage } from "./AnnouncementsPage";
import { AdminAnnouncements } from "./AdminAnnouncements";
import { AdminUserManagement } from "./AdminUserManagement";
import { AdminRoomManagement } from "./AdminRoomManagement";
import { AdminHostelSettings } from "./AdminHostelSettings";
import { AdminPaymentReceipts } from "./AdminPaymentReceipts";
import { SuggestionBox } from "./SuggestionBox";
import { useAuth } from "../contexts/AuthContext";
import announcementService from "../services/announcement.service";

interface NavItem {
  label: string;
  href: string;
  isAnnouncements?: boolean;
  isSuggestionBox?: boolean;
  isUserManagement?: boolean;
  isRoomManagement?: boolean;
  isHostelSettings?: boolean;
  isPaymentReceipts?: boolean;
}

const getNavItems = (user: any): NavItem[] => {
  const baseItems: NavItem[] = [
    { label: "Home", href: "#" },
    { label: "Facilities", href: "#facilities" },
    { label: "Gallery", href: "#gallery" },
    { label: "Contact", href: "#contact" },
  ];

  if (user) {
    const isAdmin = user.role === 'admin';
    // Announcements & Suggestion Box are only for admins and students with an
    // active booked room. A student who has merely logged in sees neither.
    const canAccessBookingFeatures = isAdmin || user.hasActiveBooking === true;

    // Insert announcements before Contact
    if (canAccessBookingFeatures) {
      baseItems.splice(-1, 0, {
        label: "Announcements",
        href: "#announcements",
        isAnnouncements: true
      });
    }

    // Add User Management for admins
    if (user.role === 'admin') {
      baseItems.push({
        label: "Users",
        href: "#users",
        isUserManagement: true
      });
      baseItems.push({
        label: "Rooms",
        href: "#rooms",
        isRoomManagement: true
      });
      baseItems.push({
        label: "Receipts",
        href: "#receipts",
        isPaymentReceipts: true
      });
      baseItems.push({
        label: "Settings",
        href: "#settings",
        isHostelSettings: true
      });
    }

    // Add Suggestion Box for students with an active booking
    if (user.role === 'student' && user.hasActiveBooking === true) {
      baseItems.push({
        label: "Suggestion Box",
        href: "#suggestion-box",
        isSuggestionBox: true
      });
    }
  }

  return baseItems;
};

interface ManageMenuProps {
  isScrolled: boolean;
  onUsers: () => void;
  onRooms: () => void;
  onReceipts: () => void;
  onSettings: () => void;
}

/**
 * Consolidates the admin management tools (Users, Rooms, Receipts, Settings)
 * into a single desktop dropdown so the nav bar doesn't overflow.
 * Built from compiled classes + inline styles (the Radix dropdown-menu's
 * token classes aren't in the precompiled CSS).
 */
function ManageMenu({ isScrolled, onUsers, onRooms, onReceipts, onSettings }: ManageMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const triggerClasses = `px-4 py-2 rounded-full transition-all duration-300 flex items-center gap-1 ${
    isScrolled
      ? "text-[#0f1f3d] hover:bg-[#d4af37]/10 hover:text-[#d4af37]"
      : "text-white hover:bg-white/10"
  }`;

  const items = [
    { label: "Users", Icon: Users, onClick: onUsers },
    { label: "Rooms", Icon: Home, onClick: onRooms },
    { label: "Receipts", Icon: Receipt, onClick: onReceipts },
    { label: "Settings", Icon: Settings, onClick: onSettings },
  ];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={triggerClasses}
      >
        Manage
        <ChevronDown
          className="w-4 h-4"
          style={{ transition: "transform 0.2s ease", transform: open ? "rotate(180deg)" : "none" }}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="bg-white rounded-lg shadow-lg"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 8,
            minWidth: 200,
            border: "1px solid rgba(15,31,61,0.1)",
            padding: 6,
            zIndex: 60,
          }}
        >
          {items.map(({ label, Icon, onClick }) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onClick();
              }}
              className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-[#0f1f3d] hover:bg-[#d4af37]/10 hover:text-[#d4af37] transition-colors"
              style={{ textAlign: "left" }}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Navigation() {
  const { user, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLoginSignup, setShowLoginSignup] = useState(false);
  const [showBookingPage, setShowBookingPage] = useState(false);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [showAdminAnnouncements, setShowAdminAnnouncements] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showRoomManagement, setShowRoomManagement] = useState(false);
  const [showHostelSettings, setShowHostelSettings] = useState(false);
  const [showPaymentReceipts, setShowPaymentReceipts] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleBookNowClick = () => {
    if (user) {
      setShowBookingPage(true);
    } else {
      setShowLoginSignup(true);
    }
  };

  const handleAnnouncementsClick = () => {
    if (user?.role === 'admin') {
      setShowAdminAnnouncements(true);
    } else {
      setShowAnnouncements(true);
    }
  };

  // Fetch unread announcements count for logged-in users
  useEffect(() => {
    if (user && user.role !== 'admin' && user.hasActiveBooking) {
      const fetchUnreadCount = async () => {
        try {
          const response = await announcementService.getAnnouncements({ unreadOnly: true });
          setUnreadCount(response.data.unreadCount || 0);
        } catch (error) {
          // Failed to fetch unread count
        }
      };

      fetchUnreadCount();

      // Poll for updates every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      aria-label="Primary"
      style={{ top: "var(--banner-h, 0px)" }}
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${isScrolled
        ? "glass-effect shadow-lg border-b border-white/20"
        : "bg-transparent"
        }`}
    >
      {/* Scrim: keeps white nav text readable over bright hero imagery before scroll */}
      {!isScrolled && (
        <div className="absolute left-0 right-0 top-0 h-32 bg-gradient-to-b from-[#0f1f3d]/95 to-transparent pointer-events-none" aria-hidden="true"></div>
      )}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <motion.a
            href="#"
            className="flex items-center gap-3 group"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Crown className="w-10 h-10 text-[#d4af37] transition-transform duration-300 group-hover:scale-105" aria-hidden="true" />
            <div>
              <span className={`text-2xl transition-colors duration-300 ${isScrolled ? "text-[#0f1f3d]" : "text-white"
                }`}>
                Majesty
              </span>
              <div className={`text-xs tracking-wider transition-colors duration-300 ${isScrolled ? "text-[#d4af37]" : "text-[#d4af37]/90"
                }`}>
                HOSTEL
              </div>
            </div>
          </motion.a>

          {/* Desktop Navigation */}
          <motion.div
            className="hidden lg:flex items-center gap-1"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {getNavItems(user).map((item: NavItem, index: number) => {
              const getButtonClasses = () => {
                const baseClasses = "px-4 py-2 rounded-full transition-all duration-300";
                return `${baseClasses} ${isScrolled
                  ? "text-[#0f1f3d] hover:bg-[#d4af37]/10 hover:text-[#d4af37]"
                  : "text-white hover:bg-white/10"
                }`;
              };

              if (item.isAnnouncements) {
                return (
                  <button
                    key={index}
                    onClick={handleAnnouncementsClick}
                    className={`${getButtonClasses()} flex items-center gap-2`}
                  >
                    {item.label}
                    {unreadCount > 0 && user?.role !== 'admin' && (
                      <span
                        className="bg-red-500 text-white text-xs font-medium min-w-[1.5rem] h-5 px-2 rounded-full flex items-center justify-center"
                        aria-label={`${unreadCount} unread announcements`}
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                );
              }

              if (item.isSuggestionBox) {
                return (
                  <SuggestionBox
                    key={index}
                    trigger={
                      <button className={getButtonClasses()}>
                        {item.label}
                      </button>
                    }
                  />
                );
              }

              // Admin management tools are consolidated into the Manage dropdown on desktop
              if (item.isUserManagement || item.isRoomManagement || item.isPaymentReceipts || item.isHostelSettings) {
                return null;
              }

              return (
                <a
                  key={index}
                  href={item.href}
                  className={getButtonClasses()}
                >
                  {item.label}
                </a>
              );
            })}
            {user?.role === 'admin' && (
              <ManageMenu
                isScrolled={isScrolled}
                onUsers={() => setShowUserManagement(true)}
                onRooms={() => setShowRoomManagement(true)}
                onReceipts={() => setShowPaymentReceipts(true)}
                onSettings={() => setShowHostelSettings(true)}
              />
            )}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            className="hidden lg:flex items-center gap-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {user?.role === 'student' && (
              <>
                <Button
                  variant="gold"
                  className="rounded-full px-6 shadow-lg"
                  onClick={handleBookNowClick}
                >
                  {user?.hasActiveBooking ? "My Booking" : "Book Now"}
                </Button>
                <Button
                  variant="ghost"
                  className={`border-2 shadow-lg hover:shadow-xl transition-all duration-300 rounded-full px-6 ${isScrolled
                    ? "border-[#0f1f3d] text-[#0f1f3d] hover:bg-[#0f1f3d] hover:text-white"
                    : "border-white text-white hover:bg-[#0f1f3d] hover:text-white"
                    }`}
                  onClick={logout}
                >
                  Logout
                </Button>
              </>
            )}
            {user?.role === 'admin' && (
              <>
                <Button
                  variant="gold"
                  className="rounded-full px-6 shadow-lg"
                  onClick={() => setShowBookingPage(true)}
                >
                  View Bookings
                </Button>
                <Button
                  variant="ghost"
                  className={`border-2 shadow-lg hover:shadow-xl transition-all duration-300 rounded-full px-6 ${isScrolled
                    ? "border-[#0f1f3d] text-[#0f1f3d] hover:bg-[#0f1f3d] hover:text-white"
                    : "border-white text-white hover:bg-[#0f1f3d] hover:text-white"
                    }`}
                  onClick={logout}
                >
                  Logout
                </Button>
              </>
            )}
            {user?.role === 'parent' && (
              <Button
                variant="ghost"
                className={`border-2 shadow-lg hover:shadow-xl transition-all duration-300 rounded-full px-6 ${isScrolled
                  ? "border-[#0f1f3d] text-[#0f1f3d] hover:bg-[#0f1f3d] hover:text-white"
                  : "border-white text-white hover:bg-[#0f1f3d] hover:text-white"
                  }`}
                onClick={logout}
              >
                Logout
              </Button>
            )}
            {!user && (
              <>
                <Button
                  variant="outline"
                  className={`border-2 shadow-lg hover:shadow-xl transition-all duration-300 rounded-full px-6 group ${isScrolled
                    ? "border-[#d4af37] text-[#0f1f3d] bg-white hover:bg-[#d4af37]"
                    : "border-[#d4af37] text-[#d4af37] bg-white/10 hover:bg-[#d4af37]"
                    }`}
                  onClick={() => setShowLoginSignup(true)}
                >
                  <span className="group-hover:text-white transition-colors duration-300">Login</span>
                </Button>
                <Button
                  variant="gold"
                  className="rounded-full px-6 shadow-lg"
                  onClick={handleBookNowClick}
                >
                  Book Now
                </Button>
              </>
            )}
          </motion.div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? "text-[#0f1f3d]" : "text-white"}`} aria-hidden="true" />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? "text-[#0f1f3d]" : "text-white"}`} aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden glass-effect border-t border-white/20 overflow-hidden"
          >
            <div className="px-4 py-6 space-y-2">
              {getNavItems(user).map((item: NavItem, index: number) => (
                item.isAnnouncements ? (
                  <button
                    key={index}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleAnnouncementsClick();
                    }}
                    className="w-full text-left block px-4 py-3 text-[#0f1f3d] hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded-xl transition-all duration-300 flex items-center justify-between"
                  >
                    <span>{item.label}</span>
                    {unreadCount > 0 && user?.role !== 'admin' && (
                      <span
                        className="bg-red-500 text-white text-xs font-medium min-w-[1.5rem] h-5 px-2 rounded-full flex items-center justify-center"
                        aria-label={`${unreadCount} unread announcements`}
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                ) : item.isSuggestionBox ? (
                  <SuggestionBox
                    key={index}
                    trigger={
                      <button
                        className="w-full text-left block px-4 py-3 text-[#0f1f3d] hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded-xl transition-all duration-300"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.label}
                      </button>
                    }
                  />
                ) : item.isUserManagement ? (
                  <button
                    key={index}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setShowUserManagement(true);
                    }}
                    className="w-full text-left block px-4 py-3 text-[#0f1f3d] hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded-xl transition-all duration-300 flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                ) : item.isRoomManagement ? (
                  <button
                    key={index}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setShowRoomManagement(true);
                    }}
                    className="w-full text-left block px-4 py-3 text-[#0f1f3d] hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded-xl transition-all duration-300 flex items-center gap-2"
                  >
                    <Home className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                ) : item.isPaymentReceipts ? (
                  <button
                    key={index}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setShowPaymentReceipts(true);
                    }}
                    className="w-full text-left block px-4 py-3 text-[#0f1f3d] hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded-xl transition-all duration-300 flex items-center gap-2"
                  >
                    <Receipt className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                ) : item.isHostelSettings ? (
                  <button
                    key={index}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setShowHostelSettings(true);
                    }}
                    className="w-full text-left block px-4 py-3 text-[#0f1f3d] hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded-xl transition-all duration-300 flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                ) : (
                  <a
                    key={index}
                    href={item.href}
                    className="block px-4 py-3 text-[#0f1f3d] hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded-xl transition-all duration-300"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMobileMenuOpen(false);
                      // Scroll to section after menu closes
                      const targetId = item.href.replace('#', '');
                      setTimeout(() => {
                        if (targetId === '') {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        } else {
                          const element = document.getElementById(targetId);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                          }
                        }
                        // Update URL hash without triggering navigation
                        window.history.pushState(null, '', item.href || '#');
                      }, 350); // Wait for menu animation to complete
                    }}
                  >
                    {item.label}
                  </a>
                )
              ))}

              {!user && (
                <div className="space-y-2 mt-4">
                  <Button
                    variant="outline"
                    className="w-full border-2 border-[#d4af37] text-[#0f1f3d] bg-white hover:bg-[#d4af37] rounded-xl group"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setShowLoginSignup(true);
                    }}
                  >
                    <span className="group-hover:text-white transition-colors duration-300">Login</span>
                  </Button>
                  <Button
                    variant="gold"
                    className="w-full rounded-xl"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleBookNowClick();
                    }}
                  >
                    Book Now
                  </Button>
                </div>
              )}
              {user?.role === 'student' && (
                <div className="space-y-2 mt-4">
                  <Button
                    variant="gold"
                    className="w-full rounded-xl"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleBookNowClick();
                    }}
                  >
                    {user?.hasActiveBooking ? "My Booking" : "Book Now"}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-2 border-[#0f1f3d] text-[#0f1f3d] hover:bg-[#0f1f3d] hover:text-white rounded-xl"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      logout();
                    }}
                  >
                    Logout
                  </Button>
                </div>
              )}
              {user?.role === 'admin' && (
                <div className="space-y-2 mt-4">
                  <Button
                    variant="gold"
                    className="w-full rounded-xl"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setShowBookingPage(true);
                    }}
                  >
                    View Bookings
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-2 border-[#0f1f3d] text-[#0f1f3d] hover:bg-[#0f1f3d] hover:text-white rounded-xl"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      logout();
                    }}
                  >
                    Logout
                  </Button>
                </div>
              )}
              {user?.role === 'parent' && (
                <div className="space-y-2 mt-4">
                  <Button
                    variant="outline"
                    className="w-full border-2 border-[#0f1f3d] text-[#0f1f3d] hover:bg-[#0f1f3d] hover:text-white rounded-xl"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      logout();
                    }}
                  >
                    Logout
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <LoginSignup
        open={showLoginSignup}
        onOpenChange={setShowLoginSignup}
        onSuccess={() => setShowBookingPage(true)}
      />
      <BookingPage open={showBookingPage} onOpenChange={setShowBookingPage} />
      <AnnouncementsPage
        open={showAnnouncements}
        onOpenChange={setShowAnnouncements}
      />
      <AdminAnnouncements
        open={showAdminAnnouncements}
        onOpenChange={setShowAdminAnnouncements}
      />
      <AdminUserManagement
        open={showUserManagement}
        onOpenChange={setShowUserManagement}
      />
      <AdminRoomManagement
        open={showRoomManagement}
        onOpenChange={setShowRoomManagement}
      />
      <AdminHostelSettings
        open={showHostelSettings}
        onOpenChange={setShowHostelSettings}
      />
      <AdminPaymentReceipts
        open={showPaymentReceipts}
        onOpenChange={setShowPaymentReceipts}
      />
    </nav>
  );
}
